/**
 * The element operations a {@link Period} needs. Branded-primitive elements have
 * no instance methods, so each span type supplies its element namespace's ops
 * (e.g. `NaiveDate.compare`, `YearMonth.next`). Equality is not among them:
 * elements are branded strings, so `===` is value equality.
 */
export interface PeriodOps<T extends string> {
  compare(a: T, b: T): number;
  next(value: T): T;
  display(value: T): string;
}

export class Period<T extends string> {
  protected constructor(
    protected firstInPeriod: T,
    protected lastInPeriod: T,
    protected ops: PeriodOps<T>,
  ) {}

  public first(): T {
    return this.firstInPeriod;
  }

  public last(): T {
    return this.lastInPeriod;
  }

  public *iter(): IterableIterator<T> {
    let current = this.firstInPeriod;
    while (this.ops.compare(current, this.lastInPeriod) <= 0) {
      yield current;
      current = this.ops.next(current);
    }
  }

  public contains(arg: T): boolean {
    return (
      this.ops.compare(this.firstInPeriod, arg) <= 0 &&
      this.ops.compare(arg, this.lastInPeriod) <= 0
    );
  }

  public isOverlapping(other: Period<T>): boolean {
    return (
      this.contains(other.first()) ||
      this.contains(other.last()) ||
      other.contains(this.first()) ||
      other.contains(this.last())
    );
  }

  public isAdjacent(other: Period<T>): boolean {
    return (
      this.ops.next(this.lastInPeriod) === other.first() ||
      this.ops.next(other.last()) === this.firstInPeriod
    );
  }

  public equals(other?: Period<T>): boolean {
    if (other === undefined) {
      return false;
    }
    return this.firstInPeriod === other.first() && this.lastInPeriod === other.last();
  }
}

/**
 * Period including first and last.
 *
 * first: the first unit in the period
 * last: the last unit in the period
 */
export interface PeriodDto<T> {
  first: T;
  last: T;
}
